import { resolveTriggerRules } from './block-rules.js';
import { z } from 'zod';
import { AllowlistRange, IsoTime } from './basics.js';
import { CrowdsecSettings, DbExposureSettings, DbHealthSettings, DbSizeSettings, DiskSettings, SystemSettings, Role, WazuhFimSettings, WebEnvSettings, WebTrafficSettings, } from './settings.js';
import { BotCatalog } from './bots.js';
import { ConfigRanges, WebSettings } from './web.js';
import { rulesOf } from './web-rules.js';
/**
 * One merged settings document per server (knowledge doc section 11), produced by the
 * API from global defaults, then role/group settings, then server overrides.
 *
 * It never contains secrets: modules refer to them by name (rule 5).
 *
 * `ranges` arrived with P5.5's detection, `web` with P6.1's confirmed environment
 * and P6.6's bot rules, and `bot_catalog` with P6.3's traffic module.
 */
export const ConfigDocument = z.object({
    version: z.number().int().positive(),
    hash: z.string().min(1).max(128),
    roles: z.array(Role).min(1),
    /**
     * Permanent allowlist. A CrowdSec safety net: these are never banned — the API
     * refuses the ban, and the agent keeps CrowdSec's own `bartizo` allowlist equal
     * to it (P5.4).
     */
    allowlist: z.array(AllowlistRange).max(500).default([]),
    modules: z.object({
        disk: DiskSettings,
        wazuh_fim: WazuhFimSettings,
        crowdsec: CrowdsecSettings,
        db_size: DbSizeSettings,
        /**
         * Defaulted when absent, unlike the four above: a document stored before P4.4
         * has neither, and it still has to parse — on the agent's disk after an
         * upgrade, and as the API's latest version until something mints a new one.
         */
        db_health: DbHealthSettings.prefault({}),
        db_exposure: DbExposureSettings.prefault({}),
        web_env: WebEnvSettings.prefault({}),
        web_traffic: WebTrafficSettings.prefault({}),
        /** Defaulted when absent: a document stored before it has none (P11.10). */
        system: SystemSettings.prefault({}),
    }),
    /**
     * Cloudflare's published ranges, from the API's daily fetch (section 8.8). Only
     * in a document whose `web_env` or `web_traffic` module is on — the readers — so
     * a change to the list does not hand every database server a new version.
     */
    ranges: ConfigRanges.optional(),
    /** The confirmed web environment (P6.1) and the bot rules (P6.6). */
    web: WebSettings.optional(),
    /**
     * The API's bot catalog (section 8.7: "the API can update it without an agent
     * release"). Only where `web_env` or `web_traffic` is on — the traffic module
     * classifies with it, and detection turns the bot rules' ids into patterns with
     * it; an agent uses whichever of this and its own has the higher `revision`.
     */
    bot_catalog: BotCatalog.optional(),
});
/**
 * What each agent module is handed from a document: its own slice, by name —
 * except that the database modules share one list of targets, `crowdsec` is
 * handed the permanent allowlist it keeps CrowdSec's own allowlist equal to, and
 * `web_env` and `web_traffic` the Cloudflare ranges, the confirmed environment
 * and the API's bot catalog, and `web_env` the bot rules it enforces.
 *
 * Section 8.4 puts the targets under `db_size`, and section 8.5 and 8.6 check the
 * same databases, so `db_health` and `db_exposure` are handed that list beside
 * their own settings rather than keeping copies an administrator would have to
 * keep in step. Changing a target therefore reaches all three, which is what the
 * agent's per-slice comparison needs to see.
 */
export function moduleSlices(document) {
    const { targets } = document.modules.db_size;
    const { ranges } = document;
    return {
        ...document.modules,
        crowdsec: {
            ...document.modules.crowdsec,
            allowlist: document.allowlist,
            // P11.16: the CrowdSec triggers among the block rules, whatever the server runs.
            triggers: resolveTriggerRules(document.web?.block_rules ?? []),
        },
        // P11.8.2: the Wazuh agent's module follows file integrity monitoring's switch.
        wazuh_agent: { enabled: document.modules.wazuh_fim.enabled },
        db_health: { ...document.modules.db_health, targets },
        db_exposure: { ...document.modules.db_exposure, targets },
        web_env: {
            ...document.modules.web_env,
            cloudflare: ranges?.cloudflare ?? [],
            ...(ranges === undefined ? {} : { ranges_version: ranges.version }),
            ...(document.web?.environment === undefined ? {} : { environment: document.web.environment }),
            rules: rulesOf(document.web),
            ...(document.web?.block_rules === undefined ? {} : { block_rules: document.web.block_rules }),
            ...(document.bot_catalog === undefined ? {} : { bot_catalog: document.bot_catalog }),
        },
        web_traffic: {
            ...document.modules.web_traffic,
            cloudflare: ranges?.cloudflare ?? [],
            crawler_ranges: ranges?.crawlers ?? {},
            ...(document.web?.environment === undefined ? {} : { environment: document.web.environment }),
            ...(document.bot_catalog === undefined ? {} : { bot_catalog: document.bot_catalog }),
        },
    };
}
/**
 * What a server reports back about a version (knowledge doc section 11, step 6;
 * section 16's `config_status.state`).
 *
 * Here rather than beside the layers, because `agent-api.ts` needs it and
 * `config-layer.ts` reaches `server.ts` — importing the other way round would close
 * a cycle. One list, so the column, the agent's report and the screen agree.
 */
export const ConfigState = z.enum(['applied', 'failed', 'drift']);
/**
 * Whether a server is running what it was last told (section 11, step 6).
 *
 * `pending` is not a state anything stores: it is the server's reported version
 * being behind the newest one, or there being no report at all. Deriving it keeps
 * it true without a sweep.
 */
export const ConfigRolloutState = z.enum(['applied', 'pending', 'failed', 'drift']);
/**
 * What one server has made of its settings, without saying which server.
 *
 * `ServerDetail` carries this — section 15 lists "config status" among what the
 * server detail returns — and `ServerConfigStatus` in `config-layer.ts` adds the
 * identity for the fleet-wide list. Here rather than there because `server.ts`
 * needs it and `config-layer.ts` reaches `server.ts`, which would close a cycle.
 */
export const ConfigStatusSummary = z.object({
    /** Null before this server has ever been given a version. */
    latestVersion: z.number().int().positive().nullable(),
    reportedVersion: z.number().int().positive().nullable(),
    state: ConfigRolloutState,
    detail: z.string().max(2000).nullable(),
    reportedAt: IsoTime.nullable(),
});
//# sourceMappingURL=config.js.map